import { WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    Extension
} from "ng2-adsk-forge-viewer";
import { UnitExtension } from 'src/app/classes/viewer/units/extension';
import { Model, ModelOrigin } from 'src/app/interfaces/api/model';
import { ViewerExtensionParams } from 'src/app/interfaces/viewer/viewer-extension-params';
import { ModelService } from 'src/app/services/api/models/model.service';
import * as THREE from 'three';
import { Config } from '../../../interfaces/shared/config';
import * as Tools from '../../shared/threeDTools';
import { MetricExtension } from '../metrics/extension';
import { MetricShape } from '../shapes/metric';
import { ZoneShape } from '../shapes/zone';
import { ZoneExtension } from '../zones/extension';
import { OriginDockingPanel } from './origin-docking-panel';

export class OriginExtension extends Extension {
    private subToolbar: any; // Autodesk Forge UI ControlGroup
    private dockingPanel: OriginDockingPanel | null = null;
    public translate!: TranslateService;
    public modelService!: ModelService;
    public model!: Model;
    public origin!: ModelOrigin;
    callback!: Function;
    panel: OriginDockingPanel | null = null;
    colors = Config.colors;
    public unitExt: UnitExtension | null = null;
    public zoneExt: ZoneExtension | null = null;
    public metricExt: MetricExtension | null = null;
    declare viewer: Autodesk.Viewing.GuiViewer3D;
    saving: boolean = false;
    timer: NodeJS.Timeout | null = null;
    originButton!: any;

    metricShapes?: WritableSignal<MetricShape[]>;

    // callbacks
    event?: (event: string, message: any) => void;

    private toolbarCreatedListener!: () => void;

    // Extension must have a name
    public static override extensionName: string = 'OriginExtension';

    public activate(): boolean {
        // Called when Forge Viewer loads your extension
        return true;
    }

    public deactivate(): boolean {
        // Called when Forge Viewer loads your extension
        return true;
    }

    public load(): boolean {
        this.toolbarCreatedListener = () => {
            this.createUI();
        };

        this.viewer.addEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, this.toolbarCreatedListener);
        return true;
    }

    public createUI(): void {
        const button = new Autodesk.Viewing.UI.Button('origin-btn');

        button.addClass('origin-btn');
        button.setToolTip('origin');
        button.setIcon('origin-btn-icon');

        button.onMouseOver = () => {
            button.setIcon('hover-origin-btn-icon');
        }

        button.onMouseOut = () => {
            button.setIcon('origin-btn-icon');
        }

        button.onClick = () => {
            this.onToolbarButtonClicked();
            this.updateButtonActiveState();
        }

        this.subToolbar = this.viewer.toolbar.getControl('unifield-toolbar')
        if (!this.subToolbar) {
            this.subToolbar = new Autodesk.Viewing.UI.ControlGroup('unifield-toolbar');
            this.viewer.toolbar.addControl(this.subToolbar);
        }

        this.subToolbar.addControl(button);
        this.viewer.toolbar.addControl(this.subToolbar);

        const styleId = 'origin-extension-style';
        if (!document.getElementById(styleId)) {
            const link = document.createElement('link');
            link.id = styleId;
            link.rel = 'stylesheet';
            link.href = 'viewer/origin/origin-extension/origin-extension.css';
            document.head.appendChild(link);
        }
    };

    public init(params: ViewerExtensionParams) {
        Object.assign(this, params);

        if (this.model?.origin) {
            if (this.viewer.model) {
                this.onOriginChange(this.model.origin, false);
            } else {
                this.viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, () => {
                    this.onOriginChange(this.model.origin, false);
                });
            }
        }
    }

    public unload(): boolean {
        // Called when Forge Viewer unloads your extension
        if (this.viewer && this.toolbarCreatedListener) {
            this.viewer.removeEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, this.toolbarCreatedListener);
        }

        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }

        if (this.dockingPanel) {
            this.dockingPanel.setVisible(false);
            this.dockingPanel = null as any;
        }

        if (this.subToolbar) {
            this.viewer.toolbar.removeControl(this.subToolbar);
            this.subToolbar = null;
        }

        this.model = null as any;
        return true;
    }

    dockPanel(): void {
        if (!this.dockingPanel) {
            this.dockingPanel = new OriginDockingPanel(this.viewer, this.viewer.container, 'modelcontainer', 'Origin', {
                modelService: this.modelService,
                translate: this.translate,
                height: '500px',
                onOriginChange: (evt: ModelOrigin, save?: boolean) => this.onOriginChange(evt, save),
                model: this.model
            });
        }

        // Wait for the DOM is ready
        setTimeout(() => {
            const titleDiv = (this.dockingPanel as any)?.container?.querySelector('.docking-panel-title');

            if (titleDiv && !titleDiv.textContent?.trim()) {
                titleDiv.textContent = "Origin";
            }
        }, 0);

        if (this.dockingPanel) {
            this.dockingPanel.setVisible(true);
        }

        (this.dockingPanel as any).addVisibilityListener?.((visible: boolean) => {
            if (!visible) {
                this.dockingPanel = null;
            }

            this.updateButtonActiveState();
        });
    }

    onOriginChange(origin: ModelOrigin, save = true): void {
        this.model.origin.orientation = Tools.axisToOrientationVector(origin.axis);

        if (origin.position.lat != undefined && origin.position.lon != undefined) {
            const xyResult = Tools.getXYFromLatLonAlt2(origin.position.lat, origin.position.lon, 0);
            this.model.origin.projectLatLonXY = [xyResult[0], xyResult[1]] as [number, number];
        }

        if (origin.position.x != undefined || origin.position.y != undefined) {
            this.model.origin.projectXY = [parseFloat(String(origin.position.x || 0)), parseFloat(String(origin.position.y || 0))] as [number, number];
        }

        this.model.origin = origin;
        let up: any = !this.model.origin.up || this.model.origin.up == "z" ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
        this.viewer.navigation.setWorldUpVector(up, true, false);
        let globalOffset = this.viewer.model?.getData()?.globalOffset;

        if (globalOffset)
            this.model.offset = globalOffset;

        if (this.timer)
            clearInterval(this.timer);

        this.timer = setInterval(() => {
            if (this.timer) 
                clearInterval(this.timer)

            if (this.modelService && save)
                this.modelService.updateModel(this.model).subscribe((res: Model) => { })
        }, 500);

        if (this.zoneExt && this.zoneExt.zones) {
            this.zoneExt.zones.forEach((zone: ZoneShape) => {
                if (zone && zone.redraw) {
                    zone.redraw(origin);
                }
            });
        }

        if (this.unitExt && this.unitExt.units && Array.isArray(this.unitExt.units)) {
            this.unitExt.units.forEach((unit: any) => {
                if (unit && unit.mesh) {
                    let mesh = unit.mesh;

                    if (unit.trackers && unit.trackers.length && this.unitExt) {
                        this.unitExt.updateFromTrack({ ...unit.lastPosition, tracker: unit.trackers[0] });
                    }

                    mesh.updateMatrix();
                }
            });
        } else {
            console.warn('OriginExtension: unitExt.units is not available or not an array:', {
                unitExt: !!this.unitExt,
                units: this.unitExt?.units,
                isArray: Array.isArray(this.unitExt?.units)
            });
        }

        if (this.metricShapes && this.metricShapes.length) {
            this.metricShapes().forEach((m: MetricShape) => {
                if (m && m.updateFromTrack) {
                    m.model.origin = origin;
                    m.updateFromTrack(m.endPosition);
                }
            });
        }

        this.origin = origin;
        this.viewer.impl.invalidate(true, true, true);
        this.viewer.impl.sceneUpdated(true);
    }

    onToolbarButtonClicked() {
        if (this.dockingPanel != null) {
            if (this.dockingPanel && typeof (this.dockingPanel as any).uninitialize === 'function') {
                (this.dockingPanel as any).uninitialize();
            }

            this.dockingPanel = null;
        } else {
            this.dockPanel();
        }

        this.updateButtonActiveState();
    }

    private updateButtonActiveState() {
        const active = !!this.dockingPanel;

        if (this.originButton) {
            this.originButton.setState(active ? Autodesk.Viewing.UI.Button.State.ACTIVE : Autodesk.Viewing.UI.Button.State.INACTIVE);
        }

        const buttonElement = document.getElementById('origin-btn');
        if (buttonElement) {
            buttonElement.classList.toggle('active', active);
            buttonElement.classList.toggle('inactive', !active);
        }
    }
}
